import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
import { SESSION_RECORDING_MASK_CLASS } from "_shared/session-recording/session-recording-privacy";
import { citationSourcePath } from "ai-assistant/citations/_views/citation-source-link";
import AssistantResultTable from "ai-assistant/result-tables/assistant-result-table";
import type { AssistantResultCellParams } from "ai-assistant/result-tables/assistant-result-table-schema";
import { useId } from "react";
import { Link as RouterLink } from "react-router";

const ResultCard = styled(Card)(({ theme }) => ({
  borderColor: theme.palette.divider,
  borderStyle: "solid",
  borderWidth: "1px",
  marginTop: theme.spacing(1),
  maxWidth: "100%",
})) as typeof Card;

/**
 * The table scrolls inside the card when a column of names cannot wrap narrow enough, so the
 * assistant pane - and the page beside it - never does.
 */
const TableScroller = styled("div")({
  maxWidth: "100%",
  overflowX: "auto",
});

const NameCell = styled(TableCell)({
  // The panel is narrow, and the message around the card breaks long words anywhere. A name here
  // wraps between words and only breaks inside one that is wider than the column.
  overflowWrap: "break-word",
  wordBreak: "normal",
});

const HeadingCell = styled(TableCell)({
  overflowWrap: "normal",
  wordBreak: "normal",
});

const FigureCell = styled(TableCell)({
  whiteSpace: "nowrap",
  fontVariantNumeric: "tabular-nums",
});

function CellContent({ cell }: Readonly<{ cell: AssistantResultCellParams }>) {
  // A cell the lookup left empty draws the dash every list draws, rather than a blank (#6713).
  if (!cell.text.trim()) return <NoValueDash />;
  // The server chose the route; this keeps it inside the application all the same, as a
  // citation's link is kept.
  const path = citationSourcePath(cell.link);
  return path ? (
    <Link component={RouterLink} to={path}>
      {cell.text}
    </Link>
  ) : (
    <>{cell.text}</>
  );
}

/**
 * A lookup's figures, drawn under the answer that refers to them (#6223).
 *
 * The figures are the server's: the lookup wrote them from what it counted, and the answer text
 * points here instead of retyping them. Every person or record the reader may open is a link to its
 * page; one they may not is named, as the text names it, and not linked. Ids are never shown.
 */
export default function ResultTableCard({ table }: Readonly<{ table: AssistantResultTable }>) {
  const titleId = useId();
  return (
    <ResultCard variant="outlined" component="section" aria-labelledby={titleId}>
      <CardContent sx={{ "&:last-child": { pb: 1.5 }, px: 1.5, py: 1.5 }}>
        <Typography id={titleId} variant="subtitle2" component="h3">
          {table.title}
        </Typography>
        {table.summary && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {table.summary}
          </Typography>
        )}
        <TableScroller>
          <Table size="small" aria-labelledby={titleId} sx={{ mt: 1, "& th, & td": { px: 0.75 } }}>
            {/* The model writes the headings, and a heading can name a firm or a person (#6330). */}
            <TableHead className={SESSION_RECORDING_MASK_CLASS}>
              <TableRow>
                {table.columns.map((column) => (
                  <HeadingCell key={column.heading} align={column.numeric ? "right" : "left"} scope="col">
                    {column.heading}
                  </HeadingCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {table.rows.map((row, rowIndex) => (
                <TableRow key={`${rowIndex}-${row[0]?.text ?? ""}`}>
                  {row.map((cell, columnIndex) => {
                    const column = table.columns[columnIndex];
                    const key = `${columnIndex}-${column?.heading ?? ""}`;
                    return column?.numeric ? (
                      <FigureCell key={key} align="right">
                        <CellContent cell={cell} />
                      </FigureCell>
                    ) : (
                      <NameCell key={key} component={columnIndex === 0 ? "th" : "td"} scope={columnIndex === 0 ? "row" : undefined}>
                        <CellContent cell={cell} />
                      </NameCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroller>
        {table.rowsNotShown > 0 && (
          <Typography variant="caption" component="p" sx={{ mt: 1 }}>
            {`And ${table.rowsNotShown} more not shown here. Ask for a narrower breakdown to see them.`}
          </Typography>
        )}
        {table.note && (
          <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1 }}>
            {table.note}
          </Typography>
        )}
      </CardContent>
    </ResultCard>
  );
}
